import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { ResultsetItem } from '@models/resultset-item';
import { AppConfigAPI } from '@models/types/appconfig.d';
import { UniqueMnemonic } from '@models/unique-mnemonic';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { TrackingService } from '@services/tracking/tracking.service';
import { flatMapToArray } from '@shared/helpers/arrays';
import { backoff } from '@shared/helpers/backoff';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ResultsService {
  private environment: AppConfigAPI;
  public asOfDate: Date = null;

  private cacheResultsetIdentifiers: ResultsetItem[] = [];

  public resultSetIdentifiers$ = new BehaviorSubject<ResultsetItem[]>([]);

  private _spread: GC.Spread.Sheets.Workbook;
  public get spread(): GC.Spread.Sheets.Workbook {
    return this._spread;
  }

  public set spread(value: GC.Spread.Sheets.Workbook) {
    this._spread = value;
  }

  constructor(
    private http: HttpClient,
    private auth: OpenIdConnectService,
    private trackService: TrackingService,
    private appConfig: AppConfigProvider
  ) {
    this.spread = new GC.Spread.Sheets.Workbook();
    this.appConfig.environment.subscribe(env => {
      this.environment = env.API;
    });
  }

  public setAsOfDate(fromDate) {
    // params for DATE are (year, month, day)
    const dateSplit = fromDate.split('/');
    // Date params are: year, month, day, hours, minutes, seconds, milliseconds
    const month = Number(`${dateSplit[0]}`);
    const day = Number(`${dateSplit[1]}`);
    const year = Number(`${dateSplit[2]}`);
    // fix for defect  3965, months are treated as index
    // 0 is january, 11 is december
    const asOfDate = new Date(year, month - 1, day);
    this.asOfDate = asOfDate;
  }

  public flatResults(results) {
    const flattedResults = flatMapToArray(results);
    let finalResults = [...flattedResults];
    flattedResults.forEach(element => {
      if (element.stresses && element.stresses instanceof Map) {
        const flattedStresses = flatMapToArray(element.stresses);
        // console.log({ flattedStresses });
        finalResults = [...finalResults, ...flattedStresses];
      }
    });
    // console.log({ finalResults });
    return finalResults;
  }

  public async getResultSetsFromDB() {
    const resultSetApiUrl = `${this.environment.RESULTSETAPI}/api/resultset`;
    const httpresponse: any = await this.http.get(resultSetApiUrl).toPromise();
    return httpresponse;
  }
  public async getSummaryFromDB() {
    const resultSetApiUrl = `${this.environment.RESULTSETAPI}/api/resultset/summary`;
    const httpresponse: any = await this.http.get(resultSetApiUrl).toPromise();

    const resultsets = httpresponse.sort(this.sortResultsets);
    resultsets.forEach(resultset => {
      if (!resultset.Is_Stress) {
        resultset.StressList = resultsets.filter(x => x.RunID_Used === resultset.RunID).sort(this.sortResultsets);
      }
      resultset.InMemory = false;
    });
    return resultsets;
  }
  private sortResultsets(a: ResultsetItem, b: ResultsetItem) {
    if (!a.Order || a.Order === -1 || a.Hidden) {
      return 1;
    }
    if (!b.Order || b.Order === -1 || b.Hidden) {
      return -1;
    }
    return a.Order - b.Order;
  }

  public getSingleResultSetsFromDB(runID: string) {
    const resultSetApiUrl = `${this.environment.RESULTSETAPI}/api/resultset/${runID}`;
    const httpresponse = this.http
      .get(resultSetApiUrl)
      .pipe(
        map((data: ResultsetItem[]) => data.find(rs => rs.RunID === runID)),
        map(result => {
          if (!result) {
            return null;
          }
          const runDate = result.Run_Date ? new Date(`${result.Run_Date} UTC`) : new Date();
          return { ...result, Run_Date: runDate as any, InMemory: true } as ResultsetItem;
        })
      )
      .toPromise();
    return httpresponse;
  }

  public hideShowResultSet(resultset: ResultsetItem, toHide: boolean) {
    const body: HideResultsetBody = {
      ResultSet_ID: resultset.ResultSetID || -1,
      User_ID: this.auth.userId,
      Hide: toHide,
      IsTemp: resultset.IsTemp,
      TempResultSetId: resultset.TempResultSetID || -1,
      RunID: resultset.RunID,
    };

    const calls: Promise<any>[] = [];
    calls.push(
      this.http
        .post<HideShowResultsetResponse>(`${this.environment.RESULTSETAPI}/api/hide`, body)
        .pipe(tap(_ => this.trackService.checkTempResults().toPromise()))
        .toPromise()
    );
    calls.push(this.getSingleResultSetsFromDB(resultset.RunID));

    return Promise.all(calls);
  }

  public async deleteSavedResultSet(resultset: ResultsetItem) {
    const resultsetApiUrl = `${this.environment.RESULTSETAPI}/api/delete`;
    const body = { RunID: resultset.RunID, ResultSetID: resultset.ResultSetID || 0 };
    // console.log(`💀 deleteSavedResultSet`, { body });
    const httpresponse: any = await this.http
      .post(resultsetApiUrl, body)
      .pipe(
        catchError(err => {
          console.error(`Issue deleting resultset ID:${resultset.ResultSetID} with RunID ${resultset.RunID}`, err);
          return err;
        })
      )
      .toPromise();
    return httpresponse;
  }

  /**
   * Checks if the mnemonic passed by parameter is unique among all resultsets, temp and live.
   */
  public async isMnemonicInDB(mnemonic: string): Promise<boolean> {
    const resultsetApiUrl = `${this.environment.RESULTSETAPI}/api/mnemonic`;
    const userid = -1;
    const unique: UniqueMnemonic = {
      Type: 1,
      Mnemonic: mnemonic,
      UserID: userid,
    };
    const httpResponse: boolean = await this.http.post<boolean>(resultsetApiUrl, unique).pipe(defaultIfEmpty(false)).toPromise();
    return httpResponse;
  }

  /**
   * Returns all the resultset column values without the Results object
   */
  public getResultSetIdentifiers(): Observable<ResultsetItem[]> {
    const resultsetApiUrl = `${this.environment.RESULTSETAPI}/api/resultsetidentifiers`;

    if (this.cacheResultsetIdentifiers.length > 0) {
      return of(this.cacheResultsetIdentifiers);
    } else {
      const httpResponse: Observable<ResultsetItem[]> = this.http.get<ResultsetItem[]>(resultsetApiUrl).pipe(
        backoff(3, 250),
        defaultIfEmpty([]),
        shareReplay(1),
        tap((identifiers: ResultsetItem[]) => {
          // this.cacheResultsetIdentifiers = identifiers;
          this.resultSetIdentifiers$.next(identifiers);
        })
      );
      return httpResponse;
    }
  }

  public clearResultSetIdentifiers() {
    this.cacheResultsetIdentifiers = [];
  }

  /**
   * Calls the API to save results and all associated edit tracking data
   * Updates the "Pending changes" state in the tracking service to disable the global SAVE button
   */
  public async saveResults() {
    const saveURL = this.environment.RESULTSETAPI + '/api/save';
    const httpresponse = await this.http
      .post(saveURL, '')
      .pipe(
        catchError(err => {
          console.error(`Problem while performing global save \nStacktrace: `, err);
          return err;
        }),
        tap(x => this.trackService.setAllModelDataChangesAfterSave())
      )
      .toPromise()
      .catch(err => console.error('Error saving temp results into permanent table', err));
    return httpresponse;
  }
}

interface HideResultsetBody {
  ResultSet_ID: number;
  User_ID: number;
  Hide: boolean;
  IsTemp: boolean;
  TempResultSetId: number;
  RunID: string;
}

export interface HideShowResultsetResponse {
  Success: boolean;
  ResultSetOrder: number;
}
